import { supabase } from '../lib/supabase';

export type StorageFolder = 'cnh' | 'crlv' | 'comprovantes' | 'checklists' | 'ocorrencias' | 'geral' | 'assinaturas';

export const BUCKET_NAME = 'sistema';

// CRLV tem bucket público próprio ("crlv"), separado do bucket "sistema"
// (privado, cnh/comprovantes/assinaturas). Motivo: o endpoint que servia CRLV
// antes não tinha nenhum controle de acesso, e URL assinada (signed URL)
// expira em 1h - inviável para um valor salvo permanentemente em veiculos.crlv.
const CRLV_BUCKET_NAME = 'crlv';

// Pastas que contêm documentos privados e confidenciais (ficam no bucket "sistema")
const SENSITIVE_FOLDERS: StorageFolder[] = ['cnh', 'comprovantes', 'assinaturas'];
const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024; // 15MB

export const storageService = {
  /**
   * Valida o tamanho e tipo do arquivo antes do envio
   */
  validateFile(file: File | Blob): void {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      throw new Error(`Arquivo excede o limite máximo permitido de 15MB (Tamanho: ${(file.size / (1024 * 1024)).toFixed(2)}MB).`);
    }
  },

  /**
   * Faz o upload de um arquivo para o storage do sistema com fallback resiliente
   * Retorna a URL pública ou segura e o caminho relativo salvo.
   */
  async uploadFile(
    file: File | Blob,
    folder: StorageFolder = 'geral',
    customFileName?: string
  ): Promise<{ publicUrl: string; path: string }> {
    try {
      this.validateFile(file);

      const extension = customFileName
        ? customFileName.split('.').pop()
        : file instanceof File
        ? file.name.split('.').pop()
        : 'pdf';

      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 8);
      const cleanFileName = `${timestamp}_${randomStr}.${extension}`;

      // CRLV vai direto para o bucket público dedicado "crlv" - sem passar
      // pelo antigo endpoint /api/crlv/upload (gravava em disco local, que é
      // apagado a cada novo deploy) e sem signed URL (expira em 1h, inviável
      // para um valor salvo permanentemente em veiculos.crlv).
      if (folder === 'crlv') {
        const { error } = await supabase.storage
          .from(CRLV_BUCKET_NAME)
          .upload(cleanFileName, file, { cacheControl: '3600', upsert: false });

        if (error) {
          throw new Error(`Falha ao enviar o CRLV para o Supabase Storage: ${error.message}`);
        }

        return {
          publicUrl: this.getPublicUrl(cleanFileName, CRLV_BUCKET_NAME),
          path: cleanFileName,
        };
      }

      // Demais pastas: direto no bucket "sistema" do Supabase Storage.
      //
      // Havia aqui duas tentativas anteriores contra /api/crlv/upload (grava
      // em disco local no servidor). Foram removidas: em produção (deploy via
      // Git na Hostinger) esse diretório é recriado do zero a cada deploy, e
      // qualquer arquivo salvo nele é perdido no deploy seguinte - foi
      // exatamente essa falha que apagou os documentos de CRLV. Ir direto ao
      // Supabase Storage evita depender de disco local do servidor.
      const filePath = `${folder}/${cleanFileName}`;

      try {
        const { error } = await supabase.storage
          .from(BUCKET_NAME)
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false, // upsert: true exige permissão de UPDATE que viola a RLS pública
          });

        if (!error) {
          const fileUrl = SENSITIVE_FOLDERS.includes(folder)
            ? await this.getSignedUrl(filePath, 3600)
            : this.getPublicUrl(filePath);

          return {
            publicUrl: fileUrl,
            path: filePath,
          };
        } else {
          console.warn(`[storageService] Supabase upload error:`, error.message);
        }
      } catch (supaErr) {
        console.warn('[storageService] Supabase storage exception:', supaErr);
      }

      // 4. Fallback final resiliente: DataURL em memória para garantir que o usuário nunca perca o anexo
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => resolve('');
        reader.readAsDataURL(file);
      });

      if (dataUrl) {
        return {
          publicUrl: dataUrl,
          path: dataUrl,
        };
      }

      throw new Error('Não foi possível enviar o documento após múltiplas tentativas.');
    } catch (err: any) {
      console.error('[storageService] Falha ao enviar arquivo:', err);
      throw err;
    }
  },

  /**
   * Obtém uma Signed URL segura e temporária para arquivos confidenciais
   */
  async getSignedUrl(filePath: string, expiresInSeconds: number = 3600): Promise<string> {
    try {
      if (!filePath) return '';
      const normalizedPath = filePath.includes(`${BUCKET_NAME}/`)
        ? filePath.split(`${BUCKET_NAME}/`)[1]
        : filePath;

      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .createSignedUrl(normalizedPath, expiresInSeconds);

      if (error || !data?.signedUrl) {
        // Fallback para public URL caso o bucket esteja configurado como público
        return this.getPublicUrl(normalizedPath);
      }

      return data.signedUrl;
    } catch (e) {
      return this.getPublicUrl(filePath);
    }
  },

  /**
   * Faz upload a partir de uma string Base64 (DataURL)
   */
  async uploadBase64(
    base64DataUrl: string,
    folder: StorageFolder = 'geral',
    fileNameWithoutExt: string = 'file'
  ): Promise<{ publicUrl: string; path: string }> {
    try {
      // Extrair MIME e binário
      const matches = base64DataUrl.match(/^data:([A-Za-z-+\/]+);base64,(.+)$/);
      if (!matches || matches.length !== 3) {
        throw new Error('Formato Base64 inválido.');
      }

      const mimeType = matches[1];
      const base64Data = matches[2];
      const ext = mimeType.split('/')[1]?.replace('jpeg', 'jpg') || 'png';

      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      return await this.uploadFile(blob, folder, `${fileNameWithoutExt}.${ext}`);
    } catch (err: any) {
      console.error('[storageService] Erro ao converter e subir Base64:', err);
      throw err;
    }
  },

  /**
   * Obtém a URL pública de um arquivo salvo. `bucket` permite apontar para
   * outro bucket público (ex.: "crlv"); por padrão usa o bucket "sistema".
   */
  getPublicUrl(filePath: string, bucket: string = BUCKET_NAME): string {
    if (!filePath) return '';
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
    return data.publicUrl;
  },

  /**
   * Remove um arquivo do storage
   */
  async deleteFile(filePath: string): Promise<boolean> {
    try {
      // Detecta se a URL/caminho pertence ao bucket "crlv" (público, separado
      // do "sistema") para remover do lugar certo.
      const isCrlv = filePath.includes(`/${CRLV_BUCKET_NAME}/`);
      const bucket = isCrlv ? CRLV_BUCKET_NAME : BUCKET_NAME;
      const normalizedPath = filePath.includes(`${bucket}/`)
        ? filePath.split(`${bucket}/`).pop()!
        : filePath;

      const { error } = await supabase.storage.from(bucket).remove([normalizedPath]);
      if (error) {
        console.warn('[storageService] Erro ao remover arquivo:', error);
        return false;
      }
      return true;
    } catch (err) {
      console.error('[storageService] Erro inesperado ao deletar:', err);
      return false;
    }
  }
};
